import React, { useRef, useState } from "react";
import { RiPlayCircleLine } from "react-icons/ri";
import EventService from "service/event/EventService";
import NavigoDashboardUtils from "modules/misc/modules/navigo-module/services/NavigoDashboardUtils";

const navigoDashboardUtils = new NavigoDashboardUtils();

const buttonStyle = (loading) => ({
  border: "none",
  background: "none",
  padding: 0,
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  color: "#F97F0F",
  fontWeight: 600,
  fontSize: "12px",
  cursor: loading ? "default" : "pointer",
  fontFamily: "inherit",
});

/**
 * Plays one call recording stored in document-service: a "Play recording"
 * button that swaps itself for the browser's <audio> player.
 *
 * Recordings live in a private GCS bucket. document-service issues a signed GET
 * URL scoped to that one object for ~15 minutes; the browser streams it
 * directly, so it is set as the <audio> src rather than downloaded first. The
 * URL is fetched on the first press, and fetched again if the player errors
 * after it has expired (a replay or seek long after the first press), resuming
 * where it was.
 *
 * Shared by Appointment details (navigo-module) and the LTR call audit.
 */
function CallRecordingPlayer({ recordingId, audioStyle }) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);
  // Where to resume after a refetch, and whether this URL was already retried
  // (so a recording that is genuinely unplayable does not loop).
  const resumeAtRef = useRef(0);
  const retriedRef = useRef(false);

  const fetchUrl = (onUrl) => {
    setLoading(true);
    navigoDashboardUtils.getCallRecordingSignedUrl(
      recordingId,
      (res) => {
        setLoading(false);
        if (!res?.url) {
          EventService.emit("showError", {
            message: `Unable to load the recording: no signed URL returned`,
          });
          return;
        }
        onUrl(res.url);
      },
      (err) => {
        setLoading(false);
        EventService.emit("showError", {
          message: `Unable to load the recording: ${err?.message}`,
        });
      },
    );
  };

  const play = () => {
    if (!recordingId || url || loading) return;
    retriedRef.current = false;
    fetchUrl(setUrl);
  };

  const onAudioError = () => {
    if (retriedRef.current || loading) return;
    retriedRef.current = true;
    resumeAtRef.current = audioRef.current ? audioRef.current.currentTime || 0 : 0;
    fetchUrl(setUrl);
  };

  const onLoadedMetadata = () => {
    if (audioRef.current && resumeAtRef.current > 0) {
      audioRef.current.currentTime = resumeAtRef.current;
      resumeAtRef.current = 0;
    }
  };

  if (url) {
    return (
      <audio
        ref={audioRef}
        controls
        autoPlay
        src={url}
        onError={onAudioError}
        onLoadedMetadata={onLoadedMetadata}
        onPlaying={() => (retriedRef.current = false)}
        style={{ height: "32px", width: "100%", ...audioStyle }}
      />
    );
  }

  return (
    <button type="button" disabled={loading} onClick={play} style={buttonStyle(loading)}>
      <RiPlayCircleLine style={{ fontSize: "14px" }} />
      {loading ? `Loading...` : `Play recording`}
    </button>
  );
}

export default CallRecordingPlayer;
